import type { Span } from '@opentelemetry/api'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace-web'
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'
import { posthogClient } from '../posthog/posthog.service'

/**
 * Browser tracing into PostHog (Tracing, OTLP at /i/v1/traces): the page load
 * (document + resource timings) and every fetch/XHR call. Calls to our API
 * carry a W3C `traceparent`, so the server (apps/server/src/tracing.ts)
 * continues the same trace through its handlers, queries and model calls.
 *
 * Head-sampled at 10% of traces. Every span carries the PostHog person and
 * session (`posthogDistinctId` / `sessionId`), so a trace opens from the
 * replay it belongs to.
 */

const DEFAULT_HOST = 'https://us.i.posthog.com'
const SAMPLE_RATE = 0.1

// Only our API gets the trace header: third-party origins would fail CORS
// preflight on an unexpected header
const PROPAGATE_TO = [/^https?:\/\/(api\.)?localhost(:\d+)?\//, /^https:\/\/api\.taka\.ai\//]

class PosthogSessionSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    const distinctId = posthogClient.getDistinctId()
    const sessionId = posthogClient.getSessionId()
    if (distinctId) span.setAttribute('posthogDistinctId', distinctId)
    if (sessionId) span.setAttribute('sessionId', sessionId)
  }
  onEnd(): void {}
  forceFlush(): Promise<void> {
    return Promise.resolve()
  }
  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}

let started = false

export function startTracing(config: { environment: string; build?: string }): void {
  const token = import.meta.env.VITE_POSTHOG_KEY
  if (started || !token || typeof window === 'undefined') return
  started = true

  try {
    const host = import.meta.env.VITE_POSTHOG_HOST || DEFAULT_HOST
    const provider = new WebTracerProvider({
      resource: resourceFromAttributes({
        [ATTR_SERVICE_NAME]: 'client',
        'deployment.environment.name': config.environment,
        ...(config.build ? { [ATTR_SERVICE_VERSION]: config.build } : {}),
      }),
      sampler: new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(SAMPLE_RATE) }),
      spanProcessors: [
        new PosthogSessionSpanProcessor(),
        new BatchSpanProcessor(
          new OTLPTraceExporter({
            url: `${host}/i/v1/traces`,
            headers: { Authorization: `Bearer ${token}` },
          })
        ),
      ],
    })
    provider.register()

    // Never trace telemetry itself: PostHog ingestion (events, replay, logs,
    // these spans) would otherwise trace its own export, forever
    const ignoreUrls = [
      /posthog\.com/,
      new RegExp(`^${host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`),
    ]
    registerInstrumentations({
      tracerProvider: provider,
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({ propagateTraceHeaderCorsUrls: PROPAGATE_TO, ignoreUrls }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: PROPAGATE_TO,
          ignoreUrls,
        }),
      ],
    })

    // Deliver the tail batch when the tab goes away
    window.addEventListener('pagehide', () => {
      void provider.forceFlush()
    })
  } catch {
    // Tracing must never take down the app boot path. No console.*: the
    // console bridge would turn this into an issue on every page load.
  }
}
